import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useToast } from '../contexts/ToastContext.jsx';
import { detectPlatform } from '../lib/utils.js';
import { Lock, ArrowRight, Loader2 } from 'lucide-react';

export default function Login() {
  const { login, isAuthenticated, loading, bootstrapping } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.from || '/app/clipboard';

  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (bootstrapping) {
    return (
      <div className="grid min-h-screen place-items-center bg-bg text-zinc-400">
        <div className="flex items-center gap-2">
          <Loader2 size={16} className="animate-spin text-accent" />
          <span className="text-sm">Loading session…</span>
        </div>
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login({ username, password, platform: detectPlatform() });
      toast.success('Welcome back');
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Login failed', 'Try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center bg-bg p-4 overflow-hidden">
      {/* Background ambient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-violet-600/10 blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 h-[300px] w-[300px] rounded-full bg-indigo-600/8 blur-[100px]" />
      </div>

      <form onSubmit={submit} className="relative z-10 w-full max-w-sm animate-fade-in">
        {/* Card */}
        <div className="rounded-2xl border border-border/60 bg-panel/60 p-8 shadow-2xl backdrop-blur-xl">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="relative">
              {/* Glow ring behind logo */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-500/30 to-indigo-500/20 blur-md scale-110" />
              <div className="relative rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-900/40 to-panel p-3">
                <img
                  src="https://x9ogt2f8d1hnsynj.public.blob.vercel-storage.com/Syncvault/icon.png"
                  alt="SyncVault Logo"
                  className="h-10 w-10"
                />
              </div>
            </div>
            <div className="text-center">
              <h1 className="text-xl font-semibold tracking-tight text-zinc-100">
                Sign in to SyncVault
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Your private cross-device clipboard.
              </p>
            </div>
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div>
              <label className="label mb-1.5">Username</label>
              <input
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
            </div>
            <div>
              <label className="label mb-1.5">Password</label>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {/* Submit button with gradient shimmer */}
            <button
              className="relative w-full overflow-hidden rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-900/40 transition-all duration-150 hover:bg-accent-hover active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              disabled={busy || loading}
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <Lock size={15} />
                  Sign in
                  <ArrowRight size={15} className="ml-auto" />
                </>
              )}
            </button>
          </div>

          {/* Footer note */}
          <p className="mt-6 text-center text-[11px] text-zinc-600">
            v1.2 · local accounts only · powered by Cloudflare
          </p>
        </div>
      </form>
    </div>
  );
}
